
import { VictoryPie, VictoryTooltip } from "victory";
import { fetchWrapper } from "../../utils/misc";
import { CheckCircleFilled } from "@ant-design/icons";
import { Tooltip } from "antd";
import { useEffect, useState } from "react";
import ChartModal from "./ChartModal";
import { motion } from "framer-motion";

const ProgressDoughnutChart = ({ name, value, maxValue, width = 350, height = 350, enableModalEvent = false, data = null, id = '' }) => {
    // Ensure value does not exceed maxValue
    const clampedValue = Math.min(value, (maxValue === 0 ? 1 : maxValue));
    const percentage = (clampedValue / (maxValue === 0 ? 1 : maxValue)) * 100;
    const [progressColor, setProgressColor] = useState(getColorWithProgress(percentage));
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [quarterModalData, setQuarterModalData] = useState([]);
    const userData = JSON.parse(localStorage.getItem("userData"));

    const angle = -(180 + (percentage / 100) * 180);

    const centerX = width / 2;
    const centerY = height / 2;

    // Calculate pointer position using trigonometry
    const radius = width * 0.1; // Radius for the pointer length
    const [pointerCoords, setPointerCoords] = useState({
        x2: centerX + radius * Math.cos((angle * Math.PI) / 180),
        y2: centerY - radius * Math.sin((angle * Math.PI) / 180)
    });

    // change bg color according to percentage of progress
    function getColorWithProgress(progressPercentage) {
        if (progressPercentage >= 100) return "#52c41a";
        if (progressPercentage >= 85 && progressPercentage < 99) return "#bae637";
        if (progressPercentage >= 70 && progressPercentage < 84) return "#ffec3d";
        if (progressPercentage >= 50 && progressPercentage < 69) return "#fa541c";
        if (progressPercentage >= 25 && progressPercentage < 49) return "#fa8c16";
        if (progressPercentage === 0) return "#ededed";
        return "#DA251C";
    }

    useEffect(() => {
        const progressPercentage = (value / maxValue) * 100;
        const color = getColorWithProgress(progressPercentage);
        setProgressColor(color);
    }, [value]);

    useEffect(() => {
        const x2 = centerX + radius * Math.cos((angle * Math.PI) / 180);
        const y2 = centerY - radius * Math.sin((angle * Math.PI) / 180);

        setPointerCoords({ x2, y2 });
    }, [value, maxValue]);

    const handleOk = () => {
        setIsModalOpen(false);
    }

    const handleCancel = () => {
        setIsModalOpen(false);
    }

    return (
        <div style={{ marginTop: '1rem', textAlign: "center", position: 'relative' }} className={id}>
            {
                (value >= maxValue) &&
                <Tooltip title="You have achived your target">
                    <CheckCircleFilled
                        style={{ position: 'absolute', top: 0, right: 20, color: '#52c41a', fontSize: '16px' }}
                    />
                </Tooltip>
            }

            {/* Name and Value Display */}
            <div>
                <h6 style={{ margin: 0, marginBottom: '0.5rem', fontSize: 15 }}>{name}</h6>
                <p style={{ margin: 0, fontSize: 15, color: "#008080" }}>
                    <b>
                        {
                            value >= 10000000 ?
                                `${(value / 10000000).toFixed(2)} Cr` :
                                `${(value / 100000).toFixed(2)} Lac`
                        }
                    </b>/
                    {
                        maxValue >= 10000000 ?
                            `${(maxValue / 10000000).toFixed(2)} Cr` :
                            `${(maxValue / 100000).toFixed(2)} Lac`
                    }
                </p>
            </div>

            <svg style={{
                maxWidth: width,
                maxHeight: height
            }}>
                <g style={{ transform: "translate(0px, 25px)" }}>
                    <VictoryPie
                        standalone={false}
                        width={width}
                        height={height}
                        animate={{ duration: 750 }}
                        innerRadius={width * 0.25}
                        startAngle={90}
                        endAngle={-90}
                        data={[
                            { x: "Remaining", y: maxValue - value },
                            { x: "Achieved", y: value },
                        ]}
                        colorScale={["#EDEDED", (progressColor)]} // Achieved and remaining colors
                        labels={({ datum }) => {
                            return (value >= 10000000 ? `${(value / 10000000).toFixed(2)} Cr` : `${(value / 100000).toFixed(2)} Lac`)
                        }}
                        labelComponent={<VictoryTooltip />}
                        style={{
                            parent: {
                                maxHeight: height,
                                maxWidth: width
                            },
                            data: {
                                accentColor: progressColor,
                                cursor: 'pointer'
                            }
                        }}
                        events={enableModalEvent ? [
                            {
                                target: "data",
                                eventHandlers: {
                                    onClick: async (e) => {
                                        const div = e.target.parentElement.parentElement.parentElement.parentElement;

                                        if (data?.loginId !== 0) {
                                            const reqUrl = `${localStorage.getItem("BaseUrl")}/Dar/AllEmpQuarterChartData?loginId=${data?.loginId}&Year=${data?.year}`;
                                            const result = await fetchWrapper("GET", reqUrl, null);
                                            if (result.resCode === 200) {
                                                const data = result.resData;
                                                if (div) {
                                                    const key = div.classList[0].slice(-2);
                                                    if (
                                                        div.classList.contains("target-q1") ||
                                                        div.classList.contains("target-q2") ||
                                                        div.classList.contains("target-q3") ||
                                                        div.classList.contains("target-q4") ||
                                                        div.classList.contains("revenue-q1") ||
                                                        div.classList.contains("revenue-q2") ||
                                                        div.classList.contains("revenue-q3") ||
                                                        div.classList.contains("revenue-q4")
                                                    ) {
                                                        const quarterArr = data[key];
                                                        if (Array.isArray(quarterArr)) {
                                                            setQuarterModalData(quarterArr);
                                                        }
                                                    } else {
                                                        let yearData = [];
                                                        for (let key in data) {
                                                            yearData.push(...data[key]);
                                                        }

                                                        setQuarterModalData(yearData);
                                                    }
                                                }
                                            }

                                        } else {
                                            const reqUrl = `${localStorage.getItem("BaseUrl")}/Dar/AllEmpQuarterChartData?loginId=${userData?.loginId}&Year=${data.year}`;
                                            const result = await fetchWrapper("GET", reqUrl, null);
                                            if (result.resCode === 200) {
                                                const quarterData = result.resData;
                                                if (div) {
                                                    const key = div.classList[0].slice(-2);
                                                    if (
                                                        div.classList.contains("target-q1") ||
                                                        div.classList.contains("target-q2") ||
                                                        div.classList.contains("target-q3") ||
                                                        div.classList.contains("target-q4") ||
                                                        div.classList.contains("revenue-q1") ||
                                                        div.classList.contains("revenue-q2") ||
                                                        div.classList.contains("revenue-q3") ||
                                                        div.classList.contains("revenue-q4")
                                                    ) {
                                                        const quarterArr = quarterData[key];
                                                        if (Array.isArray(quarterArr)) {
                                                            setQuarterModalData(quarterArr);
                                                        }
                                                    } else {
                                                        let yearData = [];
                                                        for (let key in quarterData) {
                                                            yearData.push(...quarterData[key]);
                                                        }

                                                        setQuarterModalData(yearData);
                                                    }
                                                }
                                            }
                                        }
                                        setIsModalOpen(true);
                                    }
                                }
                            }
                        ] : []}
                    />
                    {/* Semi-Doughnut Chart */}
                    {/* Pointer Line */}
                    <motion.line
                        x1={centerX}
                        y1={centerY} // Center of the chart
                        x2={pointerCoords.x2} // Pointer endpoint
                        y2={pointerCoords.y2} // Pointer endpoint
                        stroke="#000"
                        strokeWidth={2}
                        initial={false} // Prevent resetting on mount
                        animate={{
                            x2: pointerCoords.x2,
                            y2: pointerCoords.y2,
                        }}
                        transition={{
                            duration: 0.5,
                            ease: "easeInOut",
                        }}
                    />
                    {/* Pointer Dot */}
                    <circle cx={centerX} cy={centerY} r={5} fill="#000" />
                    {/* Labels */}
                    <text x={centerX - width * 0.25} y={centerY + 15} style={{ fontSize: 12, fill: "#333" }}>0</text>
                    <text x={centerX + width * 0.25 - 25} y={centerY + 15} style={{ fontSize: 12, fill: "#333", fontWeight: 700 }}>
                        {
                            maxValue >= 10000000 ?
                                `${(maxValue / 10000000).toFixed(2)} Cr` :
                                `${(maxValue / 100000).toFixed(2)} Lac`
                        }
                    </text>
                </g>
            </svg>

            <ChartModal
                handleOk={handleOk}
                handleCancel={handleCancel}
                isModalOpen={isModalOpen}
                quarterModalData={quarterModalData}
                id={id}
                year={data?.year}
            />
        </div>
    );
};

export default ProgressDoughnutChart;
